import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Alert } from 'react-bootstrap'
import { GetComprehensiveReportsUseCase } from '../../domain/usecases/Reports'
import MainCard from '../../components/theme-originals/MainCard'
import { BlockchainOverviewCard } from './components/BlockchainOverviewCard'
import { ContractMetricsCard } from './components/ContractMetricsCard'
import { FinancialMetricsCard } from './components/FinancialMetricsCard'
import { TransactionTable } from './components/TransactionTable'
import { SmartContractTable } from './components/SmartContractTable'
import { TokenMetricsCard } from './components/TokenMetricsCard'
import { BillingAnalyticsCard } from './components/BillingAnalyticsCard'
import { PDFDownloadButton } from './components/PDFDownloadButton'
import { PlatformOverviewCards } from './components/PlatformOverviewCards'
import { NetworkInfoCard } from './components/NetworkInfoCard'
import { usePermissions } from '../../hooks/usePermissions'
import * as S from './styles'
import { ReportData } from './types'

interface ReportsProps {
  remoteGetComprehensiveReports: GetComprehensiveReportsUseCase
}

const Reports: React.FC<ReportsProps> = ({ remoteGetComprehensiveReports }) => {
  const { hasPermission } = usePermissions()
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())

  useEffect(() => {
    loadReportData()
  }, [])

  const loadReportData = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await remoteGetComprehensiveReports.execute()
      setReportData(data)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error loading report data:', err)
      setError('Erro ao carregar dados do relatório. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    loadReportData()
  }

  if (error) {
    return (
      <Container fluid>
        <Alert variant="danger">
          <h4>Erro ao carregar relatório</h4>
          <p>{error}</p>
        </Alert>
      </Container>
    )
  }

  return (
    <S.ReportContainer>
      <Container fluid>
        {/* Header Section */}
        <S.ReportHeader>
          <Row>
            <Col md={8}>
              <S.ReportTitle>Relatório de Blockchain e Negócios</S.ReportTitle>
              <S.ReportSubtitle>
                Visão completa das operações da plataforma PropToken
              </S.ReportSubtitle>
              <S.LastUpdated>
                Última atualização: {lastUpdated.toLocaleString('pt-BR')}
              </S.LastUpdated>
            </Col>
            <Col md={4} className="text-right">
              <S.ActionButtonsContainer>
                <S.RefreshButton
                  variant="outline-primary"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <i className="feather icon-refresh-cw" /> Atualizar
                </S.RefreshButton>
                {hasPermission('reports.export') && (
                  <PDFDownloadButton reportData={reportData} />
                )}
              </S.ActionButtonsContainer>
            </Col>
          </Row>
        </S.ReportHeader>

        {/* Platform Overview KPIs */}
        {reportData && (
          <PlatformOverviewCards
            data={reportData.platformOverview}
            loading={loading}
          />
        )}

        {/* Main Content Grid */}
        <Row className="mb-4">
          {/* First Row */}
          <Col lg={6} xl={4} className="mb-4">
            <BlockchainOverviewCard
              data={reportData?.blockchainOverview}
              loading={loading}
            />
          </Col>
          <Col lg={6} xl={4} className="mb-4">
            <ContractMetricsCard
              data={reportData?.contractMetrics}
              loading={loading}
            />
          </Col>
          <Col lg={12} xl={4} className="mb-4">
            <FinancialMetricsCard
              data={reportData?.financialMetrics}
              loading={loading}
            />
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Second Row */}
          <Col lg={6} className="mb-4">
            <TokenMetricsCard
              data={reportData?.tokenMetrics}
              loading={loading}
            />
          </Col>
          <Col lg={6} className="mb-4">
            <BillingAnalyticsCard
              data={reportData?.billingAnalytics}
              loading={loading}
            />
          </Col>
        </Row>

        {/* Network Information Row */}
        <Row className="mb-4">
          <Col lg={12}>
            <NetworkInfoCard data={reportData?.networkInfo} loading={loading} />
          </Col>
        </Row>

        {/* Tables Section */}
        <Row className="mb-4">
          <Col lg={12}>
            <MainCard title="Transações Blockchain Recentes">
              <TransactionTable
                transactions={reportData?.recentTransactions || []}
                loading={loading}
              />
            </MainCard>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg={12}>
            <MainCard title="Smart Contracts Implantados">
              <SmartContractTable
                contracts={reportData?.smartContracts || []}
                loading={loading}
              />
            </MainCard>
          </Col>
        </Row>

        {/* Footer */}
        <S.ReportFooter>
          <p>
            Este relatório apresenta dados em tempo real da plataforma
            PropToken, incluindo todas as transações blockchain registradas e
            métricas de negócio. Todos os hashes de transação são verificáveis
            na rede Hyperledger Besu.
          </p>
        </S.ReportFooter>
      </Container>
    </S.ReportContainer>
  )
}

export default Reports
